/**
 * DeliberationHistory - Lista de Deliberações
 * 
 * Exibe histórico de deliberações do usuário.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listDeliberations, cancelDeliberation, archiveDeliberation } from '../services/deliberationService';
import type { DeliberationRun } from '../services/deliberationService';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function DeliberationHistory() {
    const navigate = useNavigate();
    const [runs, setRuns] = useState<DeliberationRun[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadRuns();
    }, []);

    async function loadRuns() {
        try {
            setLoading(true);
            const data = await listDeliberations(50, 0);
            setRuns(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erro ao carregar deliberações');
        } finally {
            setLoading(false);
        }
    }

    async function handleCancel(runId: string, event: React.MouseEvent) {
        event.stopPropagation();
        if (!confirm('Cancelar esta deliberação?')) return;

        try {
            await cancelDeliberation(runId);
            await loadRuns();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Erro ao cancelar');
        }
    }

    async function handleArchive(runId: string, event: React.MouseEvent) {
        event.stopPropagation();
        if (!confirm('Arquivar esta deliberação? Ela será removida da lista.')) return;

        try {
            await archiveDeliberation(runId);
            await loadRuns();
        } catch (err) {
            alert(err instanceof Error ? err.message : 'Erro ao arquivar');
        }
    }

    function getStatusBadge(status: string) {
        const badges = {
            completed: 'bg-green-100 text-green-800',
            running: 'bg-blue-100 text-blue-800',
            error: 'bg-red-100 text-red-800',
            timeout: 'bg-yellow-100 text-yellow-800',
        };
        return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800';
    }

    function getStatusLabel(status: string) {
        const labels = {
            completed: 'Concluída',
            running: 'Em andamento',
            error: 'Erro',
            timeout: 'Timeout',
        };
        return labels[status as keyof typeof labels] || status;
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Carregando deliberações...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={loadRuns}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Tentar novamente
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Histórico de Deliberações</h1>
                <p className="mt-2 text-gray-600">
                    Visualize e reexecute suas deliberações anteriores
                </p>
            </div>

            {runs.length === 0 ? (
                <div className="text-center py-12">
                    <p className="text-gray-500">Nenhuma deliberação encontrada</p>
                    <button
                        onClick={() => navigate('/deliberate/new')}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        Criar primeira deliberação
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {runs.map((run) => (
                        <div
                            key={run.id}
                            onClick={() => navigate(`/deliberate/${run.id}`)}
                            className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer p-6"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadge(run.status)}`}>
                                            {getStatusLabel(run.status)}
                                        </span>
                                        {run.confidence !== undefined && run.confidence !== null && (
                                            <span className="text-sm text-gray-500">
                                                Confiança: {(run.confidence * 100).toFixed(0)}%
                                            </span>
                                        )}
                                    </div>

                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {run.question}
                                    </h3>

                                    {run.final_decision && (
                                        <p className="text-gray-600 text-sm line-clamp-2">
                                            {run.final_decision}
                                        </p>
                                    )}
                                </div>

                                <div className="flex items-center gap-2 ml-4">
                                    <div className="text-right text-sm text-gray-500">
                                        <p>{formatDistanceToNow(new Date(run.created_at), { addSuffix: true, locale: ptBR })}</p>
                                        {run.completed_at && (
                                            <p className="text-xs mt-1">
                                                Duração: {Math.round((new Date(run.completed_at).getTime() - new Date(run.created_at).getTime()) / 1000)}s
                                            </p>
                                        )}
                                    </div>

                                    {run.status === 'running' && (
                                        <button
                                            onClick={(e) => handleCancel(run.id, e)}
                                            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                    )}

                                    {(run.status === 'completed' || run.status === 'error') && (
                                        <button
                                            onClick={(e) => handleArchive(run.id, e)}
                                            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                        >
                                            Arquivar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
